import {useEffect, useRef, useState} from "react";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:8000" : "");

function apiUrl(path) {
    return `${API_BASE_URL}${path}`;
}

export default function WebinarBroadcastEmailForm({
    webinarIdentifier,
    webinarTitle,
    recipientCount,
    emailsSuspended = false,
    onUnauthorized,
    onCampaignSent,
}) {
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [feedback, setFeedback] = useState({type: "idle", message: ""});
    const sendButtonRef = useRef(null);
    const confirmButtonRef = useRef(null);
    const confirmationRef = useRef(null);
    const sendInFlightRef = useRef(false);

    useEffect(() => {
        if (!isConfirmationOpen) return undefined;

        confirmButtonRef.current?.focus();

        const closeOnEscape = (event) => {
            if (event.key === "Escape" && !isSending) {
                setIsConfirmationOpen(false);
                sendButtonRef.current?.focus();
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [isConfirmationOpen, isSending]);

    const closeConfirmation = () => {
        if (isSending) return;

        setIsConfirmationOpen(false);
        sendButtonRef.current?.focus();
    };

    const requestConfirmation = (event) => {
        event.preventDefault();
        setFeedback({type: "idle", message: ""});

        if (title.trim() === "" || message.trim() === "") {
            setFeedback({
                type: "error",
                message: "من فضلك أدخل عنوان الرسالة ونصها قبل الإرسال.",
            });
            return;
        }

        if (emailsSuspended) {
            setFeedback({
                type: "error",
                message: "رسائل هذا الويبينار متوقفة. استأنف الحملات أولاً.",
            });
            return;
        }

        if (recipientCount <= 0) {
            setFeedback({
                type: "error",
                message: "لا يوجد مسجلون نشطون لإرسال الرسالة إليهم.",
            });
            return;
        }

        setIsConfirmationOpen(true);
    };

    const sendEmail = async () => {
        if (sendInFlightRef.current) return;

        const token = localStorage.getItem("webinarAdminToken") || "";

        if (!token) {
            setIsConfirmationOpen(false);
            setFeedback({
                type: "error",
                message: "انتهت جلسة الإدارة. من فضلك سجّل الدخول مرة أخرى.",
            });
            return;
        }

        sendInFlightRef.current = true;
        setIsSending(true);
        setFeedback({type: "idle", message: ""});

        try {
            const response = await fetch(
                apiUrl(`/api/admin/webinars/${encodeURIComponent(webinarIdentifier)}/emails/send`),
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        title: title.trim(),
                        message: message.trim(),
                    }),
                }
            );
            const data = await response.json().catch(() => ({}));

            if (response.status === 401) {
                onUnauthorized?.();
                return;
            }

            if (!response.ok) {
                throw new Error(data.message || "تعذر إرسال الرسالة.");
            }

            const sentCount = data.recipientCount ?? recipientCount;
            setTitle("");
            setMessage("");
            setIsConfirmationOpen(false);
            setFeedback({
                type: "success",
                message: `تم إرسال الرسالة بنجاح إلى ${sentCount} من المسجلين النشطين.`,
            });
            onCampaignSent?.();
        } catch (error) {
            setIsConfirmationOpen(false);
            setFeedback({
                type: "error",
                message: error.message || "تعذر إرسال الرسالة. من فضلك حاول مرة أخرى.",
            });
        } finally {
            sendInFlightRef.current = false;
            setIsSending(false);
            sendButtonRef.current?.focus();
        }
    };

    const keepFocusInsideConfirmation = (event) => {
        if (event.key !== "Tab") return;

        const focusableElements = confirmationRef.current?.querySelectorAll(
            "button:not(:disabled)"
        );

        if (!focusableElements?.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    return (
        <section className="webinar-broadcast" aria-labelledby="webinar-broadcast-title">
            <div className="webinar-broadcast-heading">
                <div>
                    <p className="webinar-broadcast-eyebrow">رسالة جماعية</p>
                    <h2 id="webinar-broadcast-title">إرسال رسالة إلى المسجلين</h2>
                </div>
                <p className="webinar-broadcast-recipient-count">
                    <strong>{recipientCount}</strong>
                    <span>مسجل نشط</span>
                </p>
            </div>

            <form className="webinar-broadcast-form" onSubmit={requestConfirmation}>
                <label htmlFor="webinar-email-title">عنوان الرسالة</label>
                <input
                    id="webinar-email-title"
                    type="text"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    maxLength={200}
                    required
                    disabled={isSending || emailsSuspended}
                    placeholder="اكتب عنوان الرسالة"
                />

                <label htmlFor="webinar-email-message">نص الرسالة</label>
                <textarea
                    id="webinar-email-message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    maxLength={50000}
                    rows={8}
                    required
                    disabled={isSending || emailsSuspended}
                    placeholder="اكتب الرسالة التي ستصل إلى جميع المسجلين النشطين"
                />

                <p className="webinar-broadcast-note">
                    {emailsSuspended
                        ? "رسائل هذا الويبينار متوقفة. استأنف الحملات لتتمكن من الإرسال."
                        : "لن تصل الرسالة إلى من ألغوا اشتراكهم في هذا الويبينار."}
                </p>

                <button
                    ref={sendButtonRef}
                    className="webinar-broadcast-submit"
                    type="submit"
                    disabled={isSending || emailsSuspended || recipientCount <= 0}
                >
                    إرسال إلى جميع المسجلين
                </button>

                {feedback.message && (
                    <p
                        className={`webinar-broadcast-feedback webinar-broadcast-feedback--${feedback.type}`}
                        role={feedback.type === "error" ? "alert" : "status"}
                        aria-live="polite"
                    >
                        {feedback.message}
                    </p>
                )}
            </form>

            {isConfirmationOpen && (
                <div
                    className="webinar-email-confirmation-backdrop"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeConfirmation();
                    }}
                >
                    <section
                        ref={confirmationRef}
                        className="webinar-email-confirmation"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="webinar-email-confirmation-title"
                        aria-describedby="webinar-email-confirmation-description"
                        onKeyDown={keepFocusInsideConfirmation}
                    >
                        <p className="webinar-email-confirmation-icon" aria-hidden="true">!</p>
                        <h2 id="webinar-email-confirmation-title">تأكيد إرسال الرسالة</h2>
                        <p id="webinar-email-confirmation-description">
                            سيتم إرسال هذه الرسالة الآن إلى <strong>{recipientCount}</strong> من المسجلين
                            النشطين في ويبينار «{webinarTitle || "بدون عنوان"}». لا يمكن التراجع بعد الإرسال.
                        </p>
                        <div className="webinar-email-confirmation-actions">
                            <button type="button" onClick={closeConfirmation} disabled={isSending}>
                                إلغاء
                            </button>
                            <button
                                ref={confirmButtonRef}
                                className="webinar-email-confirmation-send"
                                type="button"
                                onClick={sendEmail}
                                disabled={isSending}
                            >
                                {isSending ? "جاري الإرسال..." : "تأكيد وإرسال"}
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </section>
    );
}
